/**
 * Thermal badge stickers: QR (→ /p/:token) + the person's basic data next to it (name, CI,
 * district) so QRs never get mixed up. One label per page; the label size is configurable
 * (remembered per device) with common presets.
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ReceiptQrCode } from '@/features/receipts/components/ReceiptQrCode';
import { PrintPortal } from './PrintPortal';
import { badgeQrUrl } from './badgeQrUrl';
import {
  MIN_QR_MM,
  STICKER_PRESETS,
  nameFontPt,
  stickerGeometry,
  stickerLines,
  type StickerPerson,
} from './stickerLayout';

/** Kept as an alias: existing callers build these objects */
export type BadgeStickerData = StickerPerson;

const SIZE_KEY = 'events.badgeStickerSize';
const DEFAULT_SIZE = { width: 62, height: 40 };
const MM_TO_PX = 3.78; // @96dpi

function loadSize() {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.width > 0 && parsed.height > 0) return parsed as typeof DEFAULT_SIZE;
    }
  } catch {
    // ignore unavailable storage
  }
  return DEFAULT_SIZE;
}

function Sticker({ data, width, height }: { data: StickerPerson; width: number; height: number }) {
  const { qrMm, textWidthMm } = stickerGeometry(width, height);
  const lines = stickerLines(data);
  const nameSize = nameFontPt(data.full_name, textWidthMm, height);

  // Name + data on the left, QR at the right using the full label height
  return (
    <div
      data-print-page
      className="relative flex items-center gap-[2mm] overflow-hidden bg-white p-[2mm] text-black shadow"
      style={{ width: `${width}mm`, height: `${height}mm` }}
    >
      <div className="min-w-0 flex-1 space-y-[0.8mm] leading-tight">
        <p className="break-words font-bold leading-tight" style={{ fontSize: `${nameSize}pt` }}>
          {data.full_name}
        </p>
        <div className="space-y-[0.5mm] leading-tight" style={{ fontSize: '7.5pt' }}>
          {lines.id && <p className="font-semibold">{lines.id}</p>}
          {lines.code && <p>{lines.code}</p>}
          {data.voting_pin && <p className="font-semibold">PIN voto: {data.voting_pin}</p>}
          {lines.place && <p className="break-words">{lines.place}</p>}
          {lines.tag && (
            <p className="inline-block border border-black px-[1.2mm] font-bold tracking-wider" style={{ fontSize: '7pt' }}>
              {lines.tag}
            </p>
          )}
        </div>
      </div>
      <ReceiptQrCode url={badgeQrUrl(data.token)} size={Math.round(qrMm * MM_TO_PX)} className="shrink-0" />
    </div>
  );
}

export function BadgeStickerPrint({ stickers, onClose }: { stickers: StickerPerson[]; onClose: () => void }) {
  const [size, setSize] = useState(loadSize);

  const update = (next: { width: number; height: number }) => {
    setSize(next);
    try {
      localStorage.setItem(SIZE_KEY, JSON.stringify(next));
    } catch {
      // ignore unavailable storage
    }
  };

  const presetId = STICKER_PRESETS.find((p) => p.width === size.width && p.height === size.height)?.id ?? 'custom';
  const { qrMm } = stickerGeometry(size.width, size.height);

  return (
    <PrintPortal
      title={stickers.length === 1 ? 'Sticker con QR' : `Stickers con QR (${stickers.length})`}
      pageSize={`${size.width}mm ${size.height}mm`}
      onClose={onClose}
      dialog
      toolbar={
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Label className="text-xs">Etiqueta</Label>
          <Select
            value={presetId}
            onValueChange={(id) => {
              const preset = STICKER_PRESETS.find((p) => p.id === id);
              if (preset) update({ width: preset.width, height: preset.height });
            }}
          >
            <SelectTrigger className="h-8 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            {/* Above the print overlay (z-[100]); the default z-50 would open the list behind it */}
            <SelectContent className="z-[110]">
              {STICKER_PRESETS.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          <Label htmlFor="sticker-w" className="text-xs">Ancho (mm)</Label>
          <Input
            id="sticker-w"
            type="number"
            className="h-8 w-20"
            value={size.width}
            min={20}
            onChange={(e) => update({ ...size, width: Number(e.target.value) || DEFAULT_SIZE.width })}
          />
          <Label htmlFor="sticker-h" className="text-xs">Alto (mm)</Label>
          <Input
            id="sticker-h"
            type="number"
            className="h-8 w-20"
            value={size.height}
            min={15}
            onChange={(e) => update({ ...size, height: Number(e.target.value) || DEFAULT_SIZE.height })}
          />
          {qrMm < MIN_QR_MM && (
            <span className="flex items-center gap-1 text-xs text-amber-700">
              <AlertTriangle className="size-3.5" />
              QR de {qrMm} mm: puede costar leerlo; use una etiqueta más grande (mínimo {MIN_QR_MM} mm).
            </span>
          )}
        </div>
      }
    >
      {stickers.map((s) => (
        <Sticker key={s.token} data={s} width={size.width} height={size.height} />
      ))}
    </PrintPortal>
  );
}
